import { useQuery } from '@tanstack/react-query'
import { getApiV1WeddingsByWeddingIdDisplayActivitiesRandomNumber } from '@wedding-tech/api-client'
import { getApiV1WeddingsByWeddingIdDisplayActivitiesRandomNumberQueryKey } from '@wedding-tech/api-client/tanstack'

import type { RandomNumberState } from '@/lib/activities'
import { useDisplayWebSocket } from '@/lib/display-websocket'

/**
 * Fallback poll interval. Realtime push (useDisplayWebSocket) is the primary
 * update path — see internal/adapters/realtime — this only covers the gap
 * before the socket connects and the case where it never does (e.g. a venue
 * firewall blocking wss://).
 */
const FALLBACK_POLL_MS = 3000

/** Mirrors useWallDisplayData.ts's DisplayRequestError — see that file. */
class DisplayRequestError extends Error {
  status: number
  constructor(status: number) {
    super(`display request failed with status ${status}`)
    this.name = 'DisplayRequestError'
    this.status = status
  }
}

function isUnauthorizedError(error: unknown): boolean {
  return error instanceof DisplayRequestError && error.status === 404
}

export function useRandomNumberDisplayData(
  weddingId: string,
  token: string | null,
) {
  const request = { path: { weddingId }, query: { dt: token ?? undefined } }
  const queryKey =
    getApiV1WeddingsByWeddingIdDisplayActivitiesRandomNumberQueryKey(request)

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, response } =
        await getApiV1WeddingsByWeddingIdDisplayActivitiesRandomNumber({
          ...request,
          throwOnError: false,
        })
      if (!response.ok) throw new DisplayRequestError(response.status)
      return data
    },
    enabled: !!token,
    refetchInterval: FALLBACK_POLL_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
  })

  useDisplayWebSocket({ weddingId, dt: token, queryKey })

  return {
    state: (query.data?.data as RandomNumberState | undefined) ?? null,
    isUnauthorized: !token || isUnauthorizedError(query.error),
  }
}
